import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import update from 'immutability-helper'
import Select from 'react-select'

import { prohibitedSoftwareTags, ignoredTags } from 'Pods/Shared/Constants/tagDenylists'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import * as ValidationRules from 'Pods/Shared/Rules/Rules'
import Icon from 'Pods/Shared/Icon/Icon'
import Loader from 'Pods/Shared/Loader'
import RichEditor from 'Pods/Shared/RichEditor/RichEditor'
import TagsInputField from 'Pods/Shared/TagsInputField'

import SaveTemplateAction from './SaveTemplateAction'
import OpenTemplateAction from './OpenTemplateAction'
import TechnicalDetailsSection from './TechnicalDetailsSection'
import PricingAndLicenseSection from './PricingAndLicenseSection'

class EditorForm extends React.Component {
  constructor() {
    super()

    this.state = {
      isDataFetched: false,
      disableActions: false,
      detailsChanged: false,
      templates: [],
      templateId: '',
      templateName: '',
      productType: 'cg',
      categoryId: null,
      subcategoryId: null,
      techDetails: {
        cg: {
          textures: false,
          materials: false,
          animated: false,
          rigged: false,
          lowPoly: false,
          pbr: false,
          uvwMapping: false,
          pluginsUsed: false,
          unwrappedUvs: null,
          geometryType: null,
        },
        printable: {
          units: null,
        },
      },
      description: '',
      tags: [],
      recommendedTags: [],
      price: undefined,
      isFree: false,
      license: null,
      customLicense: '',
      showErrors: false,
      descriptionHasError: false,
      tagHasError: false,
      categories: [],
    }

    this.fetchTemplatesData = this.fetchTemplatesData.bind(this)
    this.onFetchTemplatesSuccess = this.onFetchTemplatesSuccess.bind(this)
    this.setFormValidations = this.setFormValidations.bind(this)
    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.applyTemplate = this.applyTemplate.bind(this)
    this.onApplyTemplateSuccess = this.onApplyTemplateSuccess.bind(this)
    this.onApplyTemplateError = this.onApplyTemplateError.bind(this)
    this.checkIfFormIsValid = this.checkIfFormIsValid.bind(this)
    this.errorFor = this.errorFor.bind(this)
    this.handleEditorChange = this.handleEditorChange.bind(this)
    this.onTagsChange = this.onTagsChange.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.onDetailChange = this.onDetailChange.bind(this)
    this.onTypeChange = this.onTypeChange.bind(this)
    this.getCategoriesSelect = this.getCategoriesSelect.bind(this)
    this.getSubcategoriesSelect = this.getSubcategoriesSelect.bind(this)
    this.onFreeModelTrigger = this.onFreeModelTrigger.bind(this)
    this.onAiGeneratedToggle = this.onAiGeneratedToggle.bind(this)
    this.updateTemplates = this.updateTemplates.bind(this)
    this.saveTemplate = this.saveTemplate.bind(this)
    this.editTemplate = this.editTemplate.bind(this)
    this.openTemplate = this.openTemplate.bind(this)
    this.getTemplateData = this.getTemplateData.bind(this)
    this.trackUpload = this.trackUpload.bind(this)
  }

  componentWillMount() {
    this.setState({ validationErrors: run(this.state, this.setFormValidations(this.state)) })
  }

  componentDidMount() {
    this.fetchTemplatesData()
  }

  fetchTemplatesData() {
    axios.get('/api/internal/item-templates')
      .then(this.onFetchTemplatesSuccess)
      .catch((error) => this.onFetchTemplatesError(error))
  }

  onFetchTemplatesSuccess(response) {
    this.setState({
      templates: response.data.data.filter(template => template.attributes.name !== ''),
      categories: response.data.meta.categories,
      isDataFetched: true,
    })
  }

  onFetchTemplatesError(error) {
    this.setState({ isDataFetched: true })
    App.events.publish('app:notification:flash:error', error.message)
  }

  setFormValidations(state) {
    const priceIsUndefined = (typeof state.price === 'undefined') || (state.price === '')

    return (
      [
        RuleRunner('description', 'Description', ValidationRules.validateCompetitors),
        RuleRunner('tags', 'tags', ValidationRules.maxArrLength(10)),
        RuleRunner('price', 'Price', priceIsUndefined ? null : ValidationRules.max(10000), priceIsUndefined ? null : ValidationRules.decimalPrice),
      ]
    )
  }

  handleFormSubmit(e) {
    e.preventDefault()

    this.setState({ showErrors: true })

    if (!this.checkIfFormIsValid(this.state)) {
      return null
    }

    this.setState({ disableActions: true })

    const { templateId, detailsChanged } = this.state

    if (templateId === '') {
      return this.saveTemplate('', true)
    }

    if (detailsChanged) {
      return this.editTemplate(true)
    }

    this.applyTemplate(templateId)
  }

  applyTemplate(templateId) {
    const { itemIds } = this.props

    axios.post(`/api/internal/item-templates/${templateId}/apply`, {
      ids: itemIds.map(item => item.id),
    })
      .then(this.onApplyTemplateSuccess)
      .catch(this.onApplyTemplateError)
  }

  onApplyTemplateSuccess() {
    this.setState({ disableActions: false })
    this.trackUpload()
    App.events.publish('app:notification:flash:success', `Details are being applied to ${this.props.itemIds.length} items`)
    this.props.closeModal()
    this.props.clearSelection()
  }

  onApplyTemplateError() {
    this.setState({ disableActions: false })
    App.events.publish('app:notification:flash:error', 'Error while applying template')
  }

  trackUpload() {
    const {
      tags,
      price,
      categories,
      categoryId,
      subcategoryId,
    } = this.state

    const currentCategory = categories
      .find(category => category.id === categoryId)

    const currentSubcategory = currentCategory.subcategories
      .find(subcategory => subcategory.id === subcategoryId)

    const parsePrice = (number) => {
      return parseFloat(number.toFixed(2))
    }

    const parsedPrice = parsePrice(price)
    const currentTags = tags.join('_')

    window.customEventTracker({
      name: 'upload_content',
      userId: App.config.userId || '',
      eventData: {
        content_type: 'batch',
        content_category: currentCategory.title || '',
        content_tags: currentTags,
        file_format: '',
        content_subcategory: currentSubcategory.title || '',
        model_price: parsedPrice,
      }
    })
  }

  checkIfFormIsValid(state) {
    if (state.descriptionHasError || state.tagHasError) {
      return false
    }

    return Object.getOwnPropertyNames(state.validationErrors).length === 0
  }

  errorFor(field) {
    return this.state.validationErrors[field] || ''
  }

  handleEditorChange(value, hasError) {
    const newState = update(this.state, { description: { $set: value }, descriptionHasError: { $set: hasError } })
    newState.validationErrors = run(newState, this.setFormValidations(newState))
    newState.detailsChanged = true

    this.setState(newState)
  }

  onTagsChange(tags, hasError) {
    const lastTag = tags[tags.length - 1]
    const normalizedTag = lastTag && lastTag.toLowerCase().replace(/\s+/g, '-')
    if (normalizedTag && prohibitedSoftwareTags.includes(normalizedTag)) {
      App.events.publish('app:notification:flash:error', "This tag can't be used. Please enter a different one")
      tags.splice(-1, 1)
    } else if (ignoredTags.includes(lastTag)) {
      tags.splice(-1, 1)
    }

    const newState = update(this.state, { tags: { $set: tags }, tagHasError: { $set: hasError } })
    newState.validationErrors = run(newState, this.setFormValidations(newState))
    newState.detailsChanged = true

    axios.get('/api/internal/tags/suggest', { params: { tags } })
      .then((response) => this.setState({ recommendedTags: response.data.map(tag => tag.tag) }))
      .catch(() => { })

    this.setState(newState)
  }

  handleInputChange(e) {
    const newState = update(this.state, { [e.target.id]: { $set: e.target.value } })
    newState.validationErrors = run(newState, this.setFormValidations(newState))
    newState.detailsChanged = true

    this.setState(newState)
  }

  onDetailChange(label, value) {
    const newState = update(this.state, {
      techDetails: { [this.state.productType]: { [label]: { $set: value } } },
    })

    if (label === 'uvwMapping') {
      newState.techDetails.cg.unwrappedUvs = null
    }

    newState.detailsChanged = true

    this.setState(newState)
  }

  onTypeChange(productType) {
    const newState = update(this.state, { productType: { $set: productType }, categoryId: { $set: null }, subcategoryId: { $set: null } })

    newState.detailsChanged = true

    this.setState(newState)
  }

  getCategoriesSelect() {
    const categoriesOptions = [{ value: null, label: 'Choose category' }]
    const categories = this.state.categories.filter(category => category.productType === this.state.productType).map(category => ({ value: category.id, label: category.title }))

    return categoriesOptions.concat(categories)
  }

  getSubcategoriesSelect() {
    const subcategoriesOptions = [{ value: null, label: 'Choose subcategory' }]
    const subcategories = this.state.categoryId === null ? [] : this.state.categories.find(category => category.id === this.state.categoryId).subcategories.map(subcategory => ({ value: subcategory.id, label: subcategory.title }))

    return subcategoriesOptions.concat(subcategories)
  }

  onFreeModelTrigger() {
    const price = this.state.isFree ? undefined : 0
    const newState = update(this.state, { price: { $set: price }, isFree: { $set: !this.state.isFree } })

    newState.detailsChanged = true

    this.setState(newState)
  }

  onAiGeneratedToggle() {
    const newState = update(this.state, { aiGenerated: { $set: !this.state.aiGenerated } })

    newState.detailsChanged = true

    this.setState(newState)
  }

  updateTemplates(templates) {
    this.setState({ templates })
  }

  getTemplateData(state, name = '') {
    const templateData = {
      product_type: state.productType,
      name: name === '' ? state.templateName : name,
      description: state.description === '<p></p>' ? '' : state.description,
      tags: state.tags,
      category_id: state.categoryId,
      subcategory_id: state.subcategoryId,
      price: state.price,
      license: state.license,
      custom_license: state.customLicense,
    }

    if (state.productType === 'cg') {
      const cgTechDetails = state.techDetails.cg

      templateData.textures = cgTechDetails.textures
      templateData.materials = cgTechDetails.materials
      templateData.animated = cgTechDetails.animated
      templateData.rigged = cgTechDetails.rigged
      templateData.low_poly = cgTechDetails.lowPoly
      templateData.pbr = cgTechDetails.pbr
      templateData.uvw_mapping = cgTechDetails.uvwMapping
      templateData.plugins_used = cgTechDetails.pluginsUsed
      templateData.unwrapped_uvs = cgTechDetails.unwrappedUvs
      templateData.geometry_type = cgTechDetails.geometryType
    } else {
      templateData.units = state.techDetails.printable.units
    }

    return templateData
  }

  saveTemplate(name, applyTemplate = false) {
    const templateData = this.getTemplateData(this.state, name)

    axios.post('/api/internal/item-templates', { item_template: templateData })
      .then((response) => this.onSaveTemplateSuccess(response, applyTemplate))
      .catch(() => this.onSaveTemplateError())
  }

  onSaveTemplateSuccess(response, applyTemplate) {
    const templateId = response.data.data.id

    if (applyTemplate) {
      this.setState({ templateId })
      this.applyTemplate(templateId)
    } else {
      App.events.publish('app:notification:flash:success', 'Template saved successfully!')

      this.setState(prevState => ({
        templates: prevState.templates.concat(response.data.data),
        templateId,
        templateName: response.data.data.attributes.name,
      }))
    }
  }

  onSaveTemplateError() {
    this.setState({ disableActions: false })
    App.events.publish('app:notification:flash:error', 'Template could not be saved.')
  }

  editTemplate(applyTemplate = false) {
    const templateData = this.getTemplateData(this.state)

    axios.put(`/api/internal/item-templates/${this.state.templateId}`, {
      item_template: templateData,
    })
      .then((response) => this.onEditTemplateSuccess(response, applyTemplate))
      .catch(() => this.onEditTemplateError())
  }

  onEditTemplateSuccess(response, applyTemplate) {
    if (applyTemplate) {
      this.applyTemplate(this.state.templateId)
    } else {
      this.setState({ disableActions: false, detailsChanged: false })
      App.events.publish('app:notification:flash:success', 'Template updated successfully!')
    }
  }

  onEditTemplateError() {
    this.setState({ disableActions: false })
    App.events.publish('app:notification:flash:error', 'Template could not be updated.')
  }

  openTemplate(template) {
    const attr = template.attributes
    const newState = this.state

    newState.isDataFetched = false
    newState.templateId = template.id
    newState.templateName = attr.name
    newState.productType = attr.productType
    newState.categoryId = attr.categoryId
    newState.subcategoryId = attr.subcategoryId
    newState.description = attr.description === '<p></p>' ? '' : attr.description
    newState.tags = attr.tags
    newState.price = attr.price ? attr.price : undefined
    newState.isFree = attr.price === 0
    newState.license = attr.license
    newState.customLicense = attr.customLicense
    newState.techDetails.cg.textures = attr.textures
    newState.techDetails.cg.materials = attr.materials
    newState.techDetails.cg.animated = attr.animated
    newState.techDetails.cg.rigged = attr.rigged
    newState.techDetails.cg.lowPoly = attr.lowPoly
    newState.techDetails.cg.pbr = attr.pbr
    newState.techDetails.cg.uvwMapping = attr.uvwMapping
    newState.techDetails.cg.pluginsUsed = attr.pluginsUsed
    newState.techDetails.cg.unwrappedUvs = attr.unwrappedUvs
    newState.techDetails.cg.geometryType = attr.geometryType
    newState.techDetails.printable.units = attr.units

    this.setState(newState)

    this.onOpenTemplate()
  }

  onOpenTemplate() {
    setTimeout(() => {
      this.setState({ isDataFetched: true })
    }, 1000)
  }

  render() {
    return (
      <Fragment>
        <div className="template-actions">
          <SaveTemplateAction
            saveTemplate={this.saveTemplate}
            editTemplate={this.editTemplate}
            templateNames={this.state.templates.length > 0 ? this.state.templates.map(temp => temp.attributes.name) : []}
            activeTemplateId={this.state.templateId}
          />
          <OpenTemplateAction
            updateTemplates={this.updateTemplates}
            openTemplate={this.openTemplate}
            templates={this.state.templates}
            activeTemplateId={this.state.templateId}
            activeTemplateName={this.state.templateName}
          />
        </div>

        {this.state.isDataFetched ? (
          <form onSubmit={this.handleFormSubmit}>
            <div className="section__details">
              <div className="modal-section__title">Details</div>

              <RichEditor
                label="Description"
                moreInfo={I18n.translate('tooltips.publisher.description')}
                value={this.state.description}
                onChange={this.handleEditorChange}
                showError={this.state.showErrors}
                errorText={this.errorFor('description')}
              />

              <TagsInputField
                id="tags"
                label="Tags"
                moreInfo={I18n.translate('tooltips.publisher.tags')}
                value={this.state.tags}
                formClassName="tags-input"
                onChange={this.onTagsChange}
                recommendedTags={this.state.recommendedTags}
                max={10}
                showError={this.state.showErrors}
                errorText={this.errorFor('tags')}
              />
            </div>

            <TechnicalDetailsSection
              handleDetailChange={this.onDetailChange}
              onTypeChange={this.onTypeChange}
              type={this.state.productType}
              cgTechDetails={this.state.techDetails.cg}
              printableTechDetails={this.state.techDetails.printable}
            />

            <div className="section__category u-mb10">
              <div className="modal-section__title">Category and subcategory</div>

              <div className="row">
                <div className="col-12 col-sm-5 col-md-4 category-section">
                  <div className="form-group form-group-grey">
                    <label>Category</label>
                    <Select
                      className="Select-outline"
                      id="category-select"
                      placeholder="Choose category"
                      options={this.getCategoriesSelect()}
                      onChange={(data) => this.setState({ categoryId: data.value, subcategoryId: null, detailsChanged: true })}
                      value={this.state.categoryId}
                      searchable
                      clearable={false}
                    />
                  </div>

                  <span className="separator" />
                </div>

                <div className="col-12 col-sm-5 col-md-4">
                  <div className="form-group form-group-grey">
                    <label>Subcategory</label>
                    <Select
                      className="Select-outline"
                      id="subcategory-select"
                      placeholder="Choose subcategory"
                      disabled={this.state.categoryId === null}
                      options={this.getSubcategoriesSelect()}
                      onChange={(data) => this.setState({ subcategoryId: data.value, detailsChanged: true })}
                      value={this.state.subcategoryId}
                      searchable
                      clearable={false}
                    />
                  </div>
                </div>
              </div>
            </div>

            <PricingAndLicenseSection
              isFree={this.state.isFree}
              price={this.state.price}
              license={this.state.license}
              customLicense={this.state.customLicense}
              handleInputChange={this.handleInputChange}
              onFreeModelTrigger={this.onFreeModelTrigger}
              onAiGeneratedToggle={this.onAiGeneratedToggle}
              onLicenseChange={(data) => this.setState({ license: data.value })}
              errorFor={this.errorFor}
              showErrors={this.state.showErrors}
            />

            <div className="section__footer u-text-right">
              <button
                type="button"
                className="btn btn-fat btn-outline-default btn-white btn-cancel"
                onClick={this.props.closeModal}
                disabled={this.state.disableActions}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-fat btn-primary u-ml10 btn-submit"
                disabled={this.state.disableActions}
              >
                Apply to {this.props.itemIds.length} models
              </button>
              <div className="u-mt10 text-light-grey submit-note">
                <small>
                  <Icon type={['fas', 'info-circle']} />
                  Please note it may take some time for a large amount of models to update.
                </small>
              </div>
            </div>
          </form>
        ) : (
          <Loader />
        )}
      </Fragment>
    )
  }
}

EditorForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  itemIds: PropTypes.array.isRequired,
  clearSelection: PropTypes.func.isRequired,
}

export default EditorForm
